import React, { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import {
  FaComments,
  FaTimes,
  FaPaperPlane,
  FaMinus,
  FaUser,
  FaExpand,
} from "react-icons/fa";
import styles from "./ChatWidget.module.css";

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [nameEntered, setNameEntered] = useState(false);
  const [userName, setUserName] = useState("");
  const [tempName, setTempName] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [staffOnline, setStaffOnline] = useState(0);
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const [visitorId, setVisitorId] = useState(null);

  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  const resetChat = () => {
    if (socket) {
      socket.disconnect();
    }
    localStorage.removeItem("visitor_chat_id");
    localStorage.removeItem("visitor_name");
    setMessages([]);
    setVisitorId(null);
    setNameEntered(false);
    setUserName("");
    setTempName("");
    setIsConnected(false);
    setStaffOnline(0);
    setIsMinimized(false);
    setIsOpen(false);
  };

  const startChat = () => {
    if (!tempName.trim()) return;
    const finalName = tempName.trim();
    setUserName(finalName);
    localStorage.setItem("visitor_name", finalName);

    let storedVisitorId = localStorage.getItem("visitor_chat_id");
    if (!storedVisitorId) {
      storedVisitorId =
        "visitor_" + Date.now() + "_" + Math.random().toString(36).substr(2, 8);
      localStorage.setItem("visitor_chat_id", storedVisitorId);
    }
    setVisitorId(storedVisitorId);

    //const newSocket = io("http://localhost:3001", {
    ////  transports: ["websocket", "polling"],
    ////   reconnection: true,
    //});
    const newSocket = io("wss://ws.muigaimedicalclinic.co.ke", {
      transports: ["websocket", "polling"],
      reconnection: true,
    });
    setSocket(newSocket);

    newSocket.on("connect", () => {
      setIsConnected(true);
      newSocket.emit("identify", {
        role: "visitor",
        username: finalName,
        visitorId: storedVisitorId,
      });
    });

    newSocket.on("disconnect", () => {
      setIsConnected(false);
    });

    newSocket.on("staff_online", (count) => {
      setStaffOnline(count);
    });

    newSocket.on("previous_messages", (msgs) => {
      const myMessages = msgs.filter(
        (msg) => msg.visitorId === storedVisitorId
      );
      setMessages(myMessages);
      setTimeout(scrollToBottom, 100);
    });

    newSocket.on("chat_message", (msg) => {
      if (msg.visitorId === storedVisitorId) {
        setMessages((prev) => [...prev, msg]);
        setTimeout(scrollToBottom, 100);
      }
    });

    newSocket.on("connect_error", (err) => {
      console.error("Socket connection error:", err);
    });

    setNameEntered(true);
    setIsMinimized(false);
  };

  // Restore session if name exists
  useEffect(() => {
    const savedName = localStorage.getItem("visitor_name");
    const savedVisitorId = localStorage.getItem("visitor_chat_id");
    if (savedName && savedVisitorId) {
      setUserName(savedName);
      setTempName(savedName);
      const newSocket = io("http://localhost:3001", {
        transports: ["websocket", "polling"],
        reconnection: true,
      });
      setSocket(newSocket);
      setVisitorId(savedVisitorId);
      newSocket.on("connect", () => {
        setIsConnected(true);
        newSocket.emit("identify", {
          role: "visitor",
          username: savedName,
          visitorId: savedVisitorId,
        });
      });
      newSocket.on("disconnect", () => setIsConnected(false));
      newSocket.on("staff_online", (count) => setStaffOnline(count));
      newSocket.on("previous_messages", (msgs) => {
        const myMessages = msgs.filter(
          (msg) => msg.visitorId === savedVisitorId
        );
        setMessages(myMessages);
        setTimeout(scrollToBottom, 100);
      });
      newSocket.on("chat_message", (msg) => {
        if (msg.visitorId === savedVisitorId) {
          setMessages((prev) => [...prev, msg]);
          setTimeout(scrollToBottom, 100);
        }
      });
      setNameEntered(true);
    }
  }, []);

  useEffect(() => {
    if (socket) {
      const cleanup = () => {
        socket.off("connect");
        socket.off("disconnect");
        socket.off("staff_online");
        socket.off("previous_messages");
        socket.off("chat_message");
        socket.off("connect_error");
      };
      return cleanup;
    }
  }, [socket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && nameEntered && !isMinimized) {
      setTimeout(scrollToBottom, 200);
    }
  }, [isOpen, nameEntered, isMinimized]);

  const sendMessage = (e) => {
    e.preventDefault();
    if (input.trim() === "" || !socket || !isConnected) return;
    socket.emit("chat_message", { text: input, visitorId: visitorId });
    setInput("");
  };

  const toggleChat = () => {
    if (!isOpen) {
      setIsOpen(true);
      setIsMinimized(false);
    } else {
      // If open and not minimized, do nothing? We'll rely on minimize/close buttons.
      // But if the user clicks the floating button while chat is open and not minimized, we could minimize? That might be confusing. We'll just ignore.
    }
  };

  const minimizeChat = () => {
    setIsMinimized(true);
  };

  const restoreChat = () => {
    setIsMinimized(false);
  };

  const confirmClose = () => {
    if (
      window.confirm(
        "Are you sure you want to end this chat? All messages will be cleared."
      )
    ) {
      resetChat();
    }
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (tempName.trim()) {
      startChat();
    }
  };

  // The floating button only appears when chat is completely closed (not open and not minimized)
  const showFloatingButton = !isOpen;

  return (
    <>
      {showFloatingButton && (
        <button className={styles.chatButton} onClick={toggleChat}>
          <FaComments />
        </button>
      )}

      {isOpen && (
        <>
          {!isMinimized ? (
            <div className={styles.chatWindow}>
              <div className={styles.chatHeader}>
                <h3>Chat with Support</h3>
                <div className={styles.headerButtons}>
                  <button onClick={minimizeChat} title="Minimize">
                    <FaMinus />
                  </button>
                  <button onClick={confirmClose} title="End chat">
                    <FaTimes />
                  </button>
                </div>
              </div>

              {!nameEntered ? (
                <div className={styles.nameEntry}>
                  <div className={styles.nameEntryIcon}>
                    <FaUser />
                  </div>
                  <h4>Start a conversation</h4>
                  <p>
                    Please enter your name to begin chatting with our support
                    team.
                  </p>
                  <form onSubmit={handleNameSubmit}>
                    <input
                      type="text"
                      placeholder="Your name"
                      value={tempName}
                      onChange={(e) => setTempName(e.target.value)}
                      autoFocus
                      required
                    />
                    <button type="submit">Start Chat</button>
                  </form>
                </div>
              ) : (
                <>
                  <div className={styles.onlineStatusBar}>
                    <span
                      className={
                        staffOnline > 0 ? styles.onlineDot : styles.offlineDot
                      }
                    ></span>
                    {staffOnline > 0
                      ? `${staffOnline} staff online`
                      : "No staff online"}
                  </div>
                  <div className={styles.chatMessages} ref={chatMessagesRef}>
                    {messages.length === 0 && (
                      <div className={styles.noMessages}>
                        No messages yet. Start a conversation!
                      </div>
                    )}
                    {messages.map((msg, idx) => {
                      let displayName = msg.sender_name;
                      if (
                        msg.sender_role === "admin" ||
                        msg.sender_role === "superuser"
                      ) {
                        displayName = "Support Team";
                      }
                      return (
                        <div
                          key={idx}
                          className={`${styles.message} ${
                            msg.sender_role === "visitor"
                              ? styles.userMessage
                              : styles.supportMessage
                          }`}
                        >
                          <strong>{displayName}:</strong> {msg.message}
                          <small>
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </small>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                  <form onSubmit={sendMessage} className={styles.chatInput}>
                    <input
                      type="text"
                      placeholder="Type your message..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      disabled={!isConnected}
                    />
                    <button type="submit" disabled={!isConnected}>
                      <FaPaperPlane />
                    </button>
                  </form>
                  {!isConnected && (
                    <div className={styles.disconnected}>Reconnecting...</div>
                  )}
                </>
              )}
            </div>
          ) : (
            // Minimized view – a small bar
            <div className={styles.minimizedBar} onClick={restoreChat}>
              <div className={styles.minimizedContent}>
                <FaComments className={styles.minimizedIcon} />
                <span>Chat with Support</span>
                {staffOnline > 0 && (
                  <span className={styles.minimizedBadge}>{staffOnline}</span>
                )}
              </div>
              <button
                className={styles.restoreButton}
                onClick={(e) => {
                  e.stopPropagation();
                  restoreChat();
                }}
              >
                <FaExpand />
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ChatWidget;
